import { Entity, Input, Var } from "addeus-common-library/stores/firestore";
import {
    Collection,
    SubCollection,
} from "addeus-common-library/stores/firestore/collection";
import {
    SecurityEntity,
    SecuritySubCollection,
} from "addeus-common-library/stores/firestore/security/securityDecorators";
import moment from "moment-with-locales-es6";
import "./post/socialPost";
import type { SocialPost } from "./post/socialPost";
import { SocialProfileNetwork } from "./socialProfile";
import { ACCEPTED_IMAGE_FORMATS_ARRAY } from "/@src/utils/mediaOptions";

export enum PostType {
    Post = "Post",
    Story = "Story",
    Igtv = "Igtv",
}

export enum PostStatus {
    Draft = "Draft",
    Waiting = "Waiting",
    Late = "Late",
    Rejected = "Rejected",
    Programmed = "Programmed",
    Published = "Published",
    Error = "Error",
    Cancelled = "Cancelled",
    Publishing = "Publishing",
}

export enum MediaType {
    Image = "Image",
    Video = "Video",
    Carousel = "Carousel",
    None = "None",
    Unknown = "Unknown",
}

export enum PostMood {
    Event = "Event",
    Quote = "Quote",
    Learning = "Learning",
    Backstage = "Backstage",
    Product = "Product",
    Team = "Team",
}

export enum RejectReason {
    BadObjective = "badObjective",
    OffTopic = "offTopic",
    Other = "other",
}

const isWorkspaceOwner =
    "exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))";
const isWorkspaceUser =
    "exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid))";
const isProfileOwner =
    "exists(/databases/$(database)/documents/Profile/$(ProfileId)/owners/$(request.auth.uid))";
const isProfileUser =
    "exists(/databases/$(database)/documents/Profile/$(ProfileId)/users/$(request.auth.uid))";

const isAdmin =
    "get(/databases/$(database)/documents/UserMetadata/$(request.auth.uid)).data.role == 'admin'";

const isWorkspaceOrProfileOwner = `${isWorkspaceOwner} || ${isProfileOwner}`;
const isWorkspaceOrProfileUser = `${isWorkspaceUser} || ${isProfileUser}`;
const isWorkspaceOrProfileOwnerOrUser = `${isWorkspaceOrProfileOwner} || ${isWorkspaceOrProfileUser}`;

@Collection({
    namespace: "Post",
})
@SecurityEntity({})
export class Post extends Entity {
    @Var(moment)
    @Input("datetime", { required: true })
    publishedAt: moment = moment();

    @Var(moment)
    createdAt: moment = moment();

    @Var(PostType)
    @Input("select", {
        options: PostType,
        required: true,
    })
    type: PostType = PostType.Post;

    @Var(PostStatus)
    @Input("select", {
        options: PostStatus,
        required: true,
    })
    status: PostStatus = PostStatus.Draft;

    @Input("file", {
        multiple: true,
        accepts: ["video/*", ...ACCEPTED_IMAGE_FORMATS_ARRAY],
        cropOptions: {
            minHeight: 600,
            minWidth: 600,
            aspectRatio: 1,
            mimeType: "image/jpeg",
            quality: 0.6,
        },
        imageOptions: {
            minSize: 10 * 1024, // 10KB
            maxSize: 50 * 1024 * 1024, // 50MB
        },
        videoOptions: {
            maxSize: 75 * 1024 * 1024, // 75MB
            maxDuration: 2 * 60, // 2 minutes
            minHeight: 600,
            minWidth: 600,
        },
    })
    @Var(Array.of(String))
    medias: string[] = [];

    @Input("file", {
        multiple: true,
        accepts: ["video/*", ...ACCEPTED_IMAGE_FORMATS_ARRAY],
    })
    @Var(Array.of(String))
    inspirationMedias: string[] = [];

    @Var(String)
    @Input("textarea")
    caption: string | null = null;

    @Var(MediaType)
    @Input("select", {
        options: MediaType,
        required: true,
    })
    mediaType: MediaType = MediaType.None;

    @Var(PostMood)
    @Input("select", {
        options: PostMood,
        required: true,
    })
    mood: PostMood = PostMood.Product;

    @Var(String)
    statusMessage: string = "";

    @Var(String)
    createdBy?: string;

    @Var(String)
    validated: boolean = false;

    @Var(String)
    originalPostId: string = "";

    @Var(String)
    errorMessageClient: string = "";

    @Var(Array.of(String))
    networkInError: string[] = [];

    @Var(Boolean)
    isCustomPublication: boolean = false;

    @Var(RejectReason)
    rejectReason?: RejectReason;

    @Var(String)
    rejectComment?: string;

    @Var(Array.of(SocialProfileNetwork))
    blacklistedSocialNetworks: SocialProfileNetwork[] = [];

    @Var(Object)
    ad: object | null = null;

    @Var(String)
    summary?: string;

    @Var(moment)
    deletedAt: moment | null = null;

    @Collection({
        namespace: "SocialPost",
    })
    @SecuritySubCollection({
        get: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser} `,
        list: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
        create: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        update: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        delete: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
    })
    socialPosts = new SubCollection<SocialPost>();
}
